import { useState } from 'react';
import { CaretDown, CaretUp, Copy, FilePdf, ListChecks, FirstAid, ListNumbers, ChartLine, Table, Envelope, CalendarCheck, EnvelopeSimple, Check } from '@phosphor-icons/react';
import type {
  ArtifactContent,
  QuestionsListContent,
  ProviderSummaryContent,
  ActionPlanContent,
  SymptomTimelineContent,
  ComparisonTableContent,
  AdvocacyLetterContent,
  AppointmentPrepContent,
} from '@researcher/shared';
import { useAuth } from '../../contexts/AuthContext';
import { apiUrl } from '../../config/api';

interface ArtifactCardProps {
  artifact: ArtifactContent;
  artifactId?: string;
}

const TYPE_META: Record<string, { label: string; icon: typeof ListChecks }> = {
  questions_list: { label: 'Questions for Provider', icon: ListChecks },
  provider_summary: { label: 'Provider Summary', icon: FirstAid },
  action_plan: { label: 'Action Plan', icon: ListNumbers },
  symptom_timeline: { label: 'Symptom Timeline', icon: ChartLine },
  comparison_table: { label: 'Comparison Table', icon: Table },
  advocacy_letter: { label: 'Advocacy Letter', icon: Envelope },
  appointment_prep: { label: 'Appointment Prep', icon: CalendarCheck },
};

function QuestionsListRenderer({ content }: { content: QuestionsListContent }) {
  const categories = [...new Set(content.questions.map(q => q.category))];
  return (
    <div className="space-y-3">
      {categories.map(cat => (
        <div key={cat}>
          <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1.5">{cat}</h4>
          <ul className="space-y-2">
            {content.questions.filter(q => q.category === cat).map((q, i) => (
              <li key={i} className="text-sm text-gray-800 dark:text-gray-100">
                <span className="font-medium">{q.question}</span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 italic">{q.rationale}</p>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ProviderSummaryRenderer({ content }: { content: ProviderSummaryContent }) {
  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Chief Concern</h4>
        <p className="text-sm text-gray-800 dark:text-gray-100">{content.chiefConcern}</p>
      </div>
      {content.symptoms.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Symptoms</h4>
          <ul className="list-disc list-inside text-sm text-gray-800 dark:text-gray-100 space-y-0.5">
            {content.symptoms.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}
      {content.medications.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Medications</h4>
          <ul className="list-disc list-inside text-sm text-gray-800 dark:text-gray-100 space-y-0.5">
            {content.medications.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}
      {content.recentChanges && (
        <div>
          <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Recent Changes</h4>
          <p className="text-sm text-gray-800 dark:text-gray-100">{content.recentChanges}</p>
        </div>
      )}
      {content.questions.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Questions to Ask</h4>
          <ol className="list-decimal list-inside text-sm text-gray-800 dark:text-gray-100 space-y-0.5">
            {content.questions.map((q, i) => <li key={i}>{q}</li>)}
          </ol>
        </div>
      )}
      {content.goals && (
        <div>
          <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Goals</h4>
          <p className="text-sm text-gray-800 dark:text-gray-100">{content.goals}</p>
        </div>
      )}
    </div>
  );
}

function ActionPlanRenderer({ content }: { content: ActionPlanContent }) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{content.goal}</p>
      <ol className="space-y-2">
        {content.steps.map((step, i) => (
          <li key={i} className="flex gap-2">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-pink-100 dark:bg-pink-900/40 text-pink-600 dark:text-pink-400 text-xs font-bold flex items-center justify-center mt-0.5">
              {i + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{step.action}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 font-medium whitespace-nowrap">
                  {step.timeframe}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 italic">{step.rationale}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function SymptomTimelineRenderer({ content }: { content: SymptomTimelineContent }) {
  const severityColor = (s: number) => {
    if (s <= 2) return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
    if (s <= 3) return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300';
    return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300';
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-800 dark:text-gray-100">{content.summary}</p>
      {content.entries.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-1 pr-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Date</th>
                <th className="text-left py-1 pr-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Symptom</th>
                <th className="text-center py-1 pr-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Severity</th>
                <th className="text-left py-1 text-xs font-semibold text-gray-500 dark:text-gray-400">Notes</th>
              </tr>
            </thead>
            <tbody>
              {content.entries.map((e, i) => (
                <tr key={i} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <td className="py-1.5 pr-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">{e.date}</td>
                  <td className="py-1.5 pr-2 text-gray-800 dark:text-gray-100 font-medium">{e.symptom}</td>
                  <td className="py-1.5 pr-2 text-center">
                    <span className={`inline-block text-xs font-bold px-1.5 py-0.5 rounded ${severityColor(e.severity)}`}>
                      {e.severity}/5
                    </span>
                  </td>
                  <td className="py-1.5 text-gray-600 dark:text-gray-300">{e.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {content.patterns.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Patterns</h4>
          <ul className="list-disc list-inside text-sm text-gray-800 dark:text-gray-100 space-y-0.5">
            {content.patterns.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function ComparisonTableRenderer({ content }: { content: ComparisonTableContent }) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{content.question}</p>
      <div className="space-y-3">
        {content.options.map((opt, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">{opt.name}</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-[10px] font-semibold text-green-600 dark:text-green-400 uppercase">Pros</span>
                <ul className="mt-0.5 space-y-0.5">
                  {opt.pros.map((p, j) => (
                    <li key={j} className="text-xs text-gray-700 dark:text-gray-300 flex items-start gap-1">
                      <span className="text-green-500 mt-0.5 flex-shrink-0">+</span> {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <span className="text-[10px] font-semibold text-red-600 dark:text-red-400 uppercase">Cons</span>
                <ul className="mt-0.5 space-y-0.5">
                  {opt.cons.map((c, j) => (
                    <li key={j} className="text-xs text-gray-700 dark:text-gray-300 flex items-start gap-1">
                      <span className="text-red-500 mt-0.5 flex-shrink-0">-</span> {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            {opt.considerations && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 italic">{opt.considerations}</p>
            )}
          </div>
        ))}
      </div>
      {content.summary && (
        <div className="bg-pink-50 dark:bg-pink-900/20 rounded-lg p-2">
          <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-semibold">Summary:</span> {content.summary}</p>
        </div>
      )}
    </div>
  );
}

const LETTER_TYPE_LABELS: Record<string, string> = {
  insurance_appeal: 'Insurance Appeal',
  employer_accommodation: 'Employer Accommodation',
  provider_referral: 'Provider Referral',
};

function AdvocacyLetterRenderer({ content }: { content: AdvocacyLetterContent }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-medium">
          {LETTER_TYPE_LABELS[content.letterType] || content.letterType}
        </span>
      </div>
      <div>
        <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Subject</h4>
        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{content.subject}</p>
      </div>
      <div>
        <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Letter</h4>
        <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{content.body}</p>
      </div>
      {content.keyPoints.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Key Points</h4>
          <ul className="list-disc list-inside text-sm text-gray-800 dark:text-gray-100 space-y-0.5">
            {content.keyPoints.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}
      {content.tone && (
        <div className="bg-pink-50 dark:bg-pink-900/20 rounded-lg p-2">
          <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-semibold">Tone:</span> {content.tone}</p>
        </div>
      )}
    </div>
  );
}

const APPT_TYPE_LABELS: Record<string, string> = {
  new_provider: 'New Provider',
  follow_up: 'Follow-Up',
  specialist_referral: 'Specialist Referral',
  second_opinion: 'Second Opinion',
};

function AppointmentPrepRenderer({ content }: { content: AppointmentPrepContent }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium">
          {APPT_TYPE_LABELS[content.appointmentType] || content.appointmentType}
        </span>
      </div>
      <div>
        <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Summary</h4>
        <p className="text-sm text-gray-800 dark:text-gray-100">{content.summary}</p>
      </div>
      {content.talkingPoints.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Talking Points</h4>
          <ul className="list-disc list-inside text-sm text-gray-800 dark:text-gray-100 space-y-0.5">
            {content.talkingPoints.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}
      {content.questionsToAsk.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Questions to Ask</h4>
          <ol className="list-decimal list-inside text-sm text-gray-800 dark:text-gray-100 space-y-0.5">
            {content.questionsToAsk.map((q, i) => <li key={i}>{q}</li>)}
          </ol>
        </div>
      )}
      {content.documentsToGather.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">Documents to Gather</h4>
          <ul className="list-disc list-inside text-sm text-gray-800 dark:text-gray-100 space-y-0.5">
            {content.documentsToGather.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function artifactToPlainText(artifact: ArtifactContent): string {
  switch (artifact.type) {
    case 'questions_list': {
      const c = artifact as QuestionsListContent;
      return c.questions.map(q => `[${q.category}] ${q.question}\n  Why: ${q.rationale}`).join('\n\n');
    }
    case 'provider_summary': {
      const c = artifact as ProviderSummaryContent;
      let text = `Chief Concern: ${c.chiefConcern}\n\n`;
      if (c.symptoms.length) text += `Symptoms:\n${c.symptoms.map(s => `- ${s}`).join('\n')}\n\n`;
      if (c.medications.length) text += `Medications:\n${c.medications.map(m => `- ${m}`).join('\n')}\n\n`;
      if (c.recentChanges) text += `Recent Changes: ${c.recentChanges}\n\n`;
      if (c.questions.length) text += `Questions:\n${c.questions.map((q, i) => `${i + 1}. ${q}`).join('\n')}\n\n`;
      if (c.goals) text += `Goals: ${c.goals}`;
      return text.trim();
    }
    case 'action_plan': {
      const c = artifact as ActionPlanContent;
      let text = `Goal: ${c.goal}\n\n`;
      text += c.steps.map((s, i) => `${i + 1}. [${s.timeframe}] ${s.action}\n   Why: ${s.rationale}`).join('\n\n');
      return text;
    }
    case 'symptom_timeline': {
      const c = artifact as SymptomTimelineContent;
      let text = `${c.summary}\n\n`;
      text += c.entries.map(e => `${e.date} | ${e.symptom} | Severity: ${e.severity}/5 | ${e.notes}`).join('\n');
      if (c.patterns.length) text += `\n\nPatterns:\n${c.patterns.map(p => `- ${p}`).join('\n')}`;
      return text;
    }
    case 'comparison_table': {
      const c = artifact as ComparisonTableContent;
      let text = `${c.question}\n\n`;
      text += c.options.map(o => {
        let s = `${o.name}\n  Pros: ${o.pros.join('; ')}\n  Cons: ${o.cons.join('; ')}`;
        if (o.considerations) s += `\n  Note: ${o.considerations}`;
        return s;
      }).join('\n\n');
      if (c.summary) text += `\n\nSummary: ${c.summary}`;
      return text;
    }
    case 'advocacy_letter': {
      const c = artifact as AdvocacyLetterContent;
      let text = `Subject: ${c.subject}\n\n${c.body}`;
      if (c.keyPoints.length) text += `\n\nKey Points:\n${c.keyPoints.map(p => `- ${p}`).join('\n')}`;
      if (c.tone) text += `\n\nTone: ${c.tone}`;
      return text;
    }
    case 'appointment_prep': {
      const c = artifact as AppointmentPrepContent;
      let text = `Summary: ${c.summary}`;
      if (c.talkingPoints.length) text += `\n\nTalking Points:\n${c.talkingPoints.map(p => `- ${p}`).join('\n')}`;
      if (c.questionsToAsk.length) text += `\n\nQuestions:\n${c.questionsToAsk.map((q, i) => `${i + 1}. ${q}`).join('\n')}`;
      if (c.documentsToGather.length) text += `\n\nDocuments:\n${c.documentsToGather.map(d => `- ${d}`).join('\n')}`;
      return text;
    }
    default:
      return JSON.stringify(artifact, null, 2);
  }
}

export default function ArtifactCard({ artifact, artifactId }: ArtifactCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailed, setEmailed] = useState(false);
  const { currentUser } = useAuth();

  const meta = TYPE_META[artifact.type] || { label: 'Artifact', icon: ListChecks };
  const Icon = meta.icon;

  const handleEmail = async () => {
    if (!currentUser || !artifactId) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(apiUrl('/email/send-document'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ type: 'artifact', artifactId }),
      });
      if (res.ok) {
        setEmailed(true);
        setTimeout(() => setEmailed(false), 3000);
      }
    } catch { /* silently fail */ }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(artifactToPlainText(artifact));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const handleExportPDF = async () => {
    const { generateBrandedPdf } = await import('../../utils/brandedPdf');
    type PdfSection = import('../../utils/brandedPdf').PdfSection;
    const sections: PdfSection[] = [];
    let subtitle = 'Document';
    let filename = `romy-${artifact.type}-${new Date().toISOString().split('T')[0]}.pdf`;

    switch (artifact.type) {
      case 'questions_list': {
        const c = artifact as QuestionsListContent;
        subtitle = 'Questions for Your Provider';
        const categories = [...new Set(c.questions.map(q => q.category))];
        for (const cat of categories) {
          const catQuestions = c.questions.filter(q => q.category === cat);
          sections.push({
            title: cat,
            items: catQuestions.map((q, i) => [
              { type: 'numbered' as const, number: i + 1, text: q.question, subtext: q.rationale },
            ]).flat(),
          });
        }
        break;
      }
      case 'provider_summary': {
        const c = artifact as ProviderSummaryContent;
        subtitle = 'Provider Visit Summary';
        sections.push({ title: 'Chief Concern', items: [{ type: 'text', text: c.chiefConcern, style: 'bold' }] });
        if (c.symptoms.length) sections.push({ title: 'Symptoms', items: c.symptoms.map(s => ({ type: 'bullet' as const, text: s })) });
        if (c.medications.length) sections.push({ title: 'Medications', items: c.medications.map(m => ({ type: 'bullet' as const, text: m })) });
        if (c.recentChanges) sections.push({ title: 'Recent Changes', items: [{ type: 'text', text: c.recentChanges }] });
        if (c.questions.length) sections.push({ title: 'Questions', items: c.questions.map((q, i) => ({ type: 'numbered' as const, number: i + 1, text: q })) });
        if (c.goals) sections.push({ title: 'Goals', items: [{ type: 'text', text: c.goals }] });
        break;
      }
      case 'action_plan': {
        const c = artifact as ActionPlanContent;
        subtitle = 'Action Plan';
        sections.push({ title: 'Goal', items: [{ type: 'text', text: c.goal, style: 'bold' }] });
        sections.push({
          title: 'Steps',
          items: c.steps.map((s, i) => ({ type: 'numbered' as const, number: i + 1, text: `[${s.timeframe}] ${s.action}`, subtext: s.rationale })),
        });
        break;
      }
      case 'symptom_timeline': {
        const c = artifact as SymptomTimelineContent;
        subtitle = 'Symptom Timeline';
        sections.push({ title: 'Summary', items: [{ type: 'text', text: c.summary }] });
        sections.push({
          title: 'Timeline',
          items: c.entries.map(e => ({ type: 'bullet' as const, text: `${e.date} — ${e.symptom} (${e.severity}/5): ${e.notes}` })),
        });
        if (c.patterns.length) sections.push({ title: 'Patterns', items: c.patterns.map(p => ({ type: 'bullet' as const, text: p })) });
        break;
      }
      case 'comparison_table': {
        const c = artifact as ComparisonTableContent;
        subtitle = 'Treatment Comparison';
        if (c.question) sections.push({ title: 'Question', items: [{ type: 'text', text: c.question, style: 'bold' }] });
        for (const o of c.options) {
          sections.push({
            title: o.name,
            items: [
              ...o.pros.map(p => ({ type: 'bullet' as const, text: `Pro: ${p}` })),
              ...o.cons.map(c => ({ type: 'bullet' as const, text: `Con: ${c}` })),
              ...(o.considerations ? [{ type: 'text' as const, text: o.considerations, style: 'italic' as const }] : []),
            ],
          });
        }
        if (c.summary) sections.push({ title: 'Summary', items: [{ type: 'text', text: c.summary }] });
        break;
      }
      case 'advocacy_letter': {
        const c = artifact as AdvocacyLetterContent;
        subtitle = 'Advocacy Letter';
        sections.push({ title: c.subject, items: [{ type: 'text', text: c.body }] });
        if (c.keyPoints.length) sections.push({ title: 'Key Points', items: c.keyPoints.map(p => ({ type: 'bullet' as const, text: p })) });
        break;
      }
      case 'appointment_prep': {
        const c = artifact as AppointmentPrepContent;
        subtitle = 'Visit Prep Sheet';
        sections.push({ title: 'Overview', items: [{ type: 'text', text: c.summary }] });
        if (c.talkingPoints.length) sections.push({ title: 'Talking Points', items: c.talkingPoints.map(p => ({ type: 'bullet' as const, text: p })) });
        if (c.questionsToAsk.length) sections.push({
          title: 'Ask Your Doctor',
          items: c.questionsToAsk.map((q, i) => ({ type: 'numbered' as const, number: i + 1, text: q })),
        });
        if (c.documentsToGather.length) sections.push({ title: 'Documents to Bring', items: c.documentsToGather.map(d => ({ type: 'bullet' as const, text: d })) });
        break;
      }
    }

    await generateBrandedPdf({ title: subtitle, subtitle, sections, filename });
  };

  const renderContent = () => {
    switch (artifact.type) {
      case 'questions_list':
        return <QuestionsListRenderer content={artifact as QuestionsListContent} />;
      case 'provider_summary':
        return <ProviderSummaryRenderer content={artifact as ProviderSummaryContent} />;
      case 'action_plan':
        return <ActionPlanRenderer content={artifact as ActionPlanContent} />;
      case 'symptom_timeline':
        return <SymptomTimelineRenderer content={artifact as SymptomTimelineContent} />;
      case 'comparison_table':
        return <ComparisonTableRenderer content={artifact as ComparisonTableContent} />;
      case 'advocacy_letter':
        return <AdvocacyLetterRenderer content={artifact as AdvocacyLetterContent} />;
      case 'appointment_prep':
        return <AppointmentPrepRenderer content={artifact as AppointmentPrepContent} />;
      default:
        return <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(artifact, null, 2)}</pre>;
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-pink-200 dark:border-pink-800 bg-orange-50 dark:bg-gray-800 overflow-hidden not-prose">
      {/* Collapsed header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-orange-100 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon size={18} weight="duotone" className="text-pink-500" />
          <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{meta.label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-pink-600 dark:text-pink-400 font-medium">
            {expanded ? 'Hide' : 'View'}
          </span>
          {expanded
            ? <CaretUp size={14} className="text-pink-500" />
            : <CaretDown size={14} className="text-pink-500" />
          }
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-3 pb-3 border-t border-pink-100 dark:border-pink-900/40">
          <div className="pt-3">
            {renderContent()}
          </div>
          {/* Action buttons */}
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-pink-100 dark:border-pink-900/40">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <Copy size={14} />
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <FilePdf size={14} />
              Export PDF
            </button>
            {currentUser && artifactId && (
              <button
                onClick={handleEmail}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                {emailed ? <Check size={14} className="text-emerald-500" /> : <EnvelopeSimple size={14} />}
                {emailed ? 'Sent!' : 'Email'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
